import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Download, BarChart3, Lock, ArrowLeft, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, setAuthToken } from "@/lib/api";
import { locStyle, SECTOR_META } from "@/lib/constants";
import { Login } from "@/components/Login";
import { StartScreen } from "@/components/StartScreen";
import { SectorScreen } from "@/components/SectorScreen";
import { ProductRow } from "@/components/ProductRow";
import { AddProductModal } from "@/components/AddProductModal";
import { SummarySheet } from "@/components/SummarySheet";
import { HistorySheet } from "@/components/HistorySheet";

export default function Inventory() {
  const [auth, setAuth] = useState(() => {
    const t = localStorage.getItem("inv_token");
    const u = localStorage.getItem("inv_user");
    return t && u ? { token: t, user: JSON.parse(u) } : null;
  });
  const username = auth?.user?.name || "";
  const [catalog, setCatalog] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [sector, setSector] = useState(null);
  const [activeLoc, setActiveLoc] = useState(null);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const saveTimers = useRef({});
  const editingKeys = useRef(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, inv] = await Promise.all([api.getCatalog(), api.getActive()]);
      setCatalog(cat);
      if (inv) {
        setInventory(inv);
        setCounts(inv.counts || {});
      }
    } catch (e) {
      if (e.response?.status === 401) {
        setAuthToken(null);
        localStorage.removeItem("inv_user");
        setAuth(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auth) load();
    else setLoading(false);
  }, [auth, load]);

  const isClosed = inventory?.status === "closed";

  useEffect(() => {
    if (!inventory || inventory.status !== "active") return;
    const t = setInterval(async () => {
      try {
        const fresh = await api.getCounts(inventory.id);
        setCounts((prev) => {
          const merged = { ...fresh };
          editingKeys.current.forEach((k) => {
            if (prev[k]) merged[k] = prev[k];
          });
          return merged;
        });
      } catch (e) {}
    }, 8000);
    return () => clearInterval(t);
  }, [inventory]);

  const handleAuth = (token, user) => {
    setAuthToken(token);
    localStorage.setItem("inv_user", JSON.stringify(user));
    setAuth({ token, user });
  };

  const startInventory = async (name) => {
    const inv = await api.createInventory(name, username);
    setInventory(inv);
    setCounts({});
    setSector(null);
    toast.success("Inventaire démarré");
  };

  const pickSector = (sid) => {
    setSector(sid);
    setActiveLoc(catalog.sectors[sid].locations[0].id);
    setSearch("");
    setGroupFilter("all");
  };

  const setQty = useCallback(
    (productId, quantity) => {
      if (isClosed) {
        toast.error("Inventaire clôturé");
        return;
      }
      const key = `${productId}~${activeLoc}`;
      editingKeys.current.add(key);
      setCounts((prev) => ({
        ...prev,
        [key]: { quantity, updated_by: username, updated_at: new Date().toISOString() },
      }));
      if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
      saveTimers.current[key] = setTimeout(async () => {
        try {
          await api.updateCount(inventory.id, { product_id: productId, location: activeLoc, quantity, username });
        } catch (e) {
          toast.error("Échec de l'enregistrement");
        } finally {
          editingKeys.current.delete(key);
        }
      }, 600);
    },
    [activeLoc, inventory, username, isClosed]
  );

  const addProduct = async (payload) => {
    try {
      await api.addProduct({ ...payload, username });
      const cat = await api.getCatalog();
      setCatalog(cat);
      setAddOpen(false);
      toast.success("Produit ajouté");
    } catch (e) {
      toast.error("Échec de l'ajout");
    }
  };

  const closeInventory = async () => {
    const inv = await api.closeInventory(inventory.id);
    setInventory(inv);
    toast.success("Inventaire clôturé");
  };
  const reopenInventory = async () => {
    const inv = await api.reopenInventory(inventory.id);
    setInventory(inv);
    toast.success("Inventaire ré-ouvert");
  };
  const newInventory = () => {
    setInventory(null);
    setCounts({});
    setSector(null);
  };
  const changeUser = () => {
    setAuthToken(null);
    localStorage.removeItem("inv_user");
    setAuth(null);
    setInventory(null);
    setSector(null);
    setCounts({});
  };
  const openInventoryFromHistory = async (id) => {
    const inv = await api.getInventory(id);
    setInventory(inv);
    setCounts(inv.counts || {});
    setHistoryOpen(false);
    setSector(null);
    toast.success(inv.status === "closed" ? "Inventaire ouvert (lecture seule)" : "Inventaire ouvert");
  };

  const doExport = async (id, name) => {
    setExporting(true);
    try {
      const token = localStorage.getItem("inv_token");
      const res = await fetch(api.exportUrl(id), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("export");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(name || "inventaire").replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Fichier Excel téléchargé");
    } catch (e) {
      toast.error("Échec de l'export Excel");
    } finally {
      setExporting(false);
    }
  };

  const sectorConf = sector && catalog ? catalog.sectors[sector] : null;

  const filteredProducts = useMemo(() => {
    if (!sectorConf) return [];
    const q = search.trim().toLowerCase();
    let list = sectorConf.products;
    if (sectorConf.groups && groupFilter !== "all") list = list.filter((p) => p.group === groupFilter);
    if (q) list = list.filter((p) => p.name.toLowerCase().includes(q));
    return list;
  }, [sectorConf, search, groupFilter]);

  const sectorLocIds = useMemo(() => (sectorConf ? sectorConf.locations.map((l) => l.id) : []), [sectorConf]);

  const getTotal = useCallback(
    (pid) => {
      let t = 0;
      for (const loc of sectorLocIds) {
        const e = counts[`${pid}~${loc}`];
        if (e) t += Number(e.quantity) || 0;
      }
      return +t.toFixed(2);
    },
    [counts, sectorLocIds]
  );

  const locSubtotal = useMemo(() => {
    if (!sectorConf) return 0;
    let s = 0;
    sectorConf.products.forEach((p) => {
      const e = counts[`${p.id}~${activeLoc}`];
      if (e) s += Number(e.quantity) || 0;
    });
    return +s.toFixed(2);
  }, [sectorConf, counts, activeLoc]);

  const sectorTotal = useMemo(() => {
    if (!sectorConf) return 0;
    let s = 0;
    sectorConf.products.forEach((p) => (s += getTotal(p.id)));
    return +s.toFixed(2);
  }, [sectorConf, getTotal]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Chargement…</div>
      </div>
    );
  }

  if (!auth) return <Login onAuth={handleAuth} />;

  if (!inventory)
    return (
      <>
        <StartScreen username={username} onStart={startInventory} hasPrevious={false} onHistory={() => setHistoryOpen(true)} />
        <HistorySheet open={historyOpen} onOpenChange={setHistoryOpen} onOpenInventory={openInventoryFromHistory} onExport={doExport} currentId={null} />
      </>
    );

  if (!sector)
    return (
      <>
        <SectorScreen
          inventory={inventory}
          catalog={catalog}
          counts={counts}
          username={username}
          onPick={pickSector}
          onHistory={() => setHistoryOpen(true)}
          onClose={closeInventory}
          onReopen={reopenInventory}
          onNew={newInventory}
          onChangeUser={changeUser}
        />
        <HistorySheet open={historyOpen} onOpenChange={setHistoryOpen} onOpenInventory={openInventoryFromHistory} onExport={doExport} currentId={inventory?.id} />
      </>
    );

  const meta = SECTOR_META[sector];

  return (
    <div className="min-h-screen pb-28">
      <header className="sticky top-0 z-50 bg-[#0D0E12]/90 backdrop-blur-md border-b border-border">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="icon" data-testid="back-to-sector-btn" onClick={() => setSector(null)} className="h-9 w-9 rounded-xl shrink-0">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-display text-base font-extrabold leading-none">
                <span className={meta.accent}>{meta.label}</span>
              </h1>
              <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                {inventory.name}
                {isClosed && <span className="text-rose-400"> · lecture seule</span>}
              </div>
            </div>
          </div>
          {!isClosed && (
            <Button variant="secondary" data-testid="add-product-btn" onClick={() => setAddOpen(true)} className="h-9 rounded-xl gap-1.5 px-3 bg-secondary">
              <Plus className="h-4 w-4" /> <span className="text-xs font-semibold">Produit</span>
            </Button>
          )}
        </div>

        <div data-testid="location-tabs" className="max-w-3xl mx-auto flex gap-2 overflow-x-auto scrollbar-none px-4 pb-2.5">
          {sectorConf.locations.map((loc) => {
            const s = locStyle(loc.id);
            const active = activeLoc === loc.id;
            return (
              <button
                key={loc.id}
                data-testid={`location-tab-${loc.id}`}
                onClick={() => setActiveLoc(loc.id)}
                className={`shrink-0 h-10 px-4 rounded-xl text-sm font-semibold flex items-center gap-2 transition-all active:scale-95 ${
                  active ? s.active + " shadow-lg" : "bg-secondary text-muted-foreground hover:text-foreground"
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${active ? "bg-current opacity-80" : s.dot}`} />
                {loc.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="drink-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un produit…"
            className="h-12 pl-9 pr-9 rounded-xl bg-secondary border-border"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 px-1">
          Salle : <span className={locStyle(activeLoc).text}>{sectorConf.locations.find((l) => l.id === activeLoc)?.label}</span> · {filteredProducts.length} produits
        </p>
        {sectorConf.groups && (
          <div data-testid="group-filter" className="flex gap-2 mt-3">
            {[{ id: "all", label: "Tous" }, ...sectorConf.groups].map((g) => {
              const active = groupFilter === g.id;
              return (
                <button
                  key={g.id}
                  data-testid={`group-chip-${g.id}`}
                  onClick={() => setGroupFilter(g.id)}
                  className={`h-9 px-4 rounded-full text-sm font-semibold transition-all active:scale-95 ${
                    active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <main className="max-w-3xl mx-auto px-4 pt-2">
        <div className="rounded-2xl border border-border bg-card px-4 divide-y divide-border/50">
          {filteredProducts.map((p) => (
            <ProductRow
              key={p.id}
              product={p}
              qty={(() => {
                const e = counts[`${p.id}~${activeLoc}`];
                return e ? Number(e.quantity) || 0 : 0;
              })()}
              total={getTotal(p.id)}
              disabled={isClosed}
              onSetQty={setQty}
            />
          ))}
          {filteredProducts.length === 0 && (
            <div className="text-center text-muted-foreground py-16 text-sm">Aucun produit trouvé.</div>
          )}
        </div>
      </main>

      <div data-testid="summary-bottom-bar" className="fixed bottom-0 inset-x-0 z-50 bg-[#0D0E12]/95 backdrop-blur-md border-t border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex-1 cursor-pointer" onClick={() => setSummaryOpen(true)} data-testid="open-summary-btn">
            <div className="flex items-center gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{sectorConf.locations.find((l) => l.id === activeLoc)?.label}</div>
                <div className="font-mono-num text-lg font-extrabold" data-testid="location-subtotal-counter">{locSubtotal}</div>
              </div>
              <div className="h-8 w-px bg-border" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{meta.label}</div>
                <div className="font-mono-num text-lg font-extrabold text-primary" data-testid="grand-total-counter">{sectorTotal}</div>
              </div>
            </div>
          </div>
          <Button variant="secondary" size="icon" data-testid="summary-toggle-btn" onClick={() => setSummaryOpen(true)} className="h-12 w-12 rounded-xl bg-secondary shrink-0">
            <BarChart3 className="h-5 w-5" />
          </Button>
          <Button data-testid="export-excel-btn" onClick={() => doExport(inventory.id, inventory.name)} disabled={exporting} className="h-12 rounded-xl font-bold gap-2 px-4 active:scale-95 transition-transform">
            <Download className="h-5 w-5" />
            <span>{exporting ? "…" : "Excel"}</span>
          </Button>
        </div>
      </div>

      <AddProductModal open={addOpen} onOpenChange={setAddOpen} suppliers={sectorConf.suppliers} sectorLabel={meta.label} onSave={addProduct} />
      <SummarySheet open={summaryOpen} onOpenChange={setSummaryOpen} catalog={catalog} counts={counts} />
      <HistorySheet open={historyOpen} onOpenChange={setHistoryOpen} onOpenInventory={openInventoryFromHistory} onExport={doExport} currentId={inventory?.id} />
    </div>
  );
}
